import { Table } from "react-bootstrap"

/**
 * Lightweight table for approval request lists. Client-side only,
 * no pagination. Use DataTable instead for server-paginated lists.
 */
type Column<RowT> = {
	label: string
	accessor: keyof RowT
	render?: (row: RowT) => React.ReactNode
}

type Props<RowT> = {
	rows: RowT[]
	columns: Column<RowT>[]
	onRowClick: (row: RowT) => void
	rowKey: (row: RowT) => string | number
	emptyMessage: string
	actions?: (row: RowT) => React.ReactNode
}

export type { Column }
export default function RequestTable<RowT extends Record<string, unknown>>({
	rows,
	columns,
	onRowClick,
	rowKey,
	emptyMessage,
	actions,
}: Props<RowT>) {
	return (
		<Table striped bordered hover>
			<thead>
				<tr>
					{columns.map((c) => (
						<th key={String(c.accessor)}>{c.label}</th>
					))}
					{actions && (
						<th style={{ whiteSpace: "nowrap", width: "1%" }}>Actions</th>
					)}
				</tr>
			</thead>
			<tbody>
				{(() => {
					if (rows.length === 0) return renderEmptyRow()
					return renderDataRows()
				})()}
			</tbody>
		</Table>
	)

	function renderEmptyRow() {
		return (
			<tr>
				<td
					colSpan={actions ? columns.length + 1 : columns.length}
					className="text-center text-muted"
				>
					{emptyMessage}
				</td>
			</tr>
		)
	}

	function renderDataRows() {
		return rows.map((row) => (
			<tr
				key={rowKey(row)}
				onClick={() => onRowClick(row)}
				style={{ cursor: "pointer" }}
			>
				{columns.map((c) => renderCell(row, c))}
				{actions && (
					<td
						onClick={(e) => e.stopPropagation()}
						onKeyUp={(e) => e.stopPropagation()} //Avoids biome error
						style={{ whiteSpace: "nowrap" }}
					>
						{actions(row)}
					</td>
				)}
			</tr>
		))
	}

	function renderCell(row: RowT, c: Column<RowT>) {
		return (
			<td key={String(c.accessor)}>
				{c.render ? c.render(row) : (row[c.accessor] as React.ReactNode)}
			</td>
		)
	}
}
